import * as XLSX from "xlsx";

/** A column of an uploaded tracker source, with a value to recognise it by. */
export interface TrackerSourceColumn {
	foundField: string;
	/** First non-empty value in the column, or undefined when every row is blank. */
	sample?: string;
}

/**
 * Read an uploaded spreadsheet down to what the mapping step needs: the header names, and one
 * real value per column (PREC-3417).
 *
 * The sample matters more than it looks. Files exported from a CRM routinely have headers like
 * "Field 4", and the whole ticket is about users mapping the wrong column and getting a source
 * that tracks nobody — seeing `linkedin.com/in/jdoe` next to the name is what makes the right
 * choice obvious.
 *
 * Only the first sheet is read, matching what the API's `parseAndTrimXlsx` imports.
 */
export async function readTrackerSourceColumns(file: File): Promise<TrackerSourceColumn[]> {
	const workbook = XLSX.read(await file.arrayBuffer());
	const sheet = workbook.Sheets[workbook.SheetNames[0]];

	if (!sheet) {
		return [];
	}

	const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: "" });
	const headers = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1, blankrows: false })[0];

	return (headers ?? [])
		.map((header) => (header ?? "").toString().trim())
		.filter(Boolean)
		.map((foundField) => ({
			foundField,
			sample: rows
				.map((row) => (row[foundField] ?? "").toString().trim())
				.find((value) => value.length > 0),
		}));
}
